import React, { useMemo, useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query";
import Label, { useLabels } from "components/general/Label";
import { useViewport } from 'components/general/useViewPort'
import MirsTable from "components/general/table/Table"
import { Stack } from 'react-bootstrap'
import Noc from "components/presentation/Noc";
import Competitor from 'components/presentation/Competitor';
import Gender from "components/presentation/Gender";
import DateTime from "components/presentation/DateTime";
import { GGetSportsCode, getSportCodeEntityObjects } from "components/common/SportCode";
import { AthleteEntriesExpanded } from "components/common/entries/AthleteEntriesExpanded";
import { ColumnExpander } from "components/common/TableColumns";
import { useUrlFriendlyToParameters } from 'helpers/urlFriendly';
import RegisteredEvents from "components/presentation/RegisteredEvents";
import { ALL_URL, getDateTimeFormat, getHeight } from "helpers/utils";
import { useGetPositions } from "helpers/commonCodes";

const getConfiguration = (breakPoint, sportEntryCodes, sportEntryACodes, event, hasEnteries, isAlphabeticalSort) => {
  const isMobile = (breakPoint === "sm" || breakPoint === "xs")
  let configuration = {
    columns: [],
    sortBy: [],
    disableSort: false
  };
  let hasPosition = hasEnteries?.find(e => e === 'POSITION');
  configuration.sortBy = isMobile ? [{ id: "NOC", desc: false }] : (hasPosition ? [{ id: 'NOC', desc: false }, { id: 'POSITION', desc: false }, { id: 'Competitor', desc: false }] : [{ id: 'NOC', desc: false }, { id: 'Competitor', desc: false }]);
  configuration.columns = isMobile ? TableColumnsSM(isAlphabeticalSort) : TableColumns(sportEntryCodes, sportEntryACodes, event, hasPosition, hasEnteries, isAlphabeticalSort)
  return configuration;
};


const EventEntriesAthletesTable = ({ data }) => {
  const { breakPoint } = useViewport();
  const isMobile = (breakPoint === "sm" || breakPoint === "xs")
  const { mirs_discipline, mirs_competition, mirs_language, mirs_event } = useUrlFriendlyToParameters()
  const [sportEntryCodes, setSportEntryCodes] = useState([])
  const [sportEntryACodes, setSportEntryACodes] = useState([])
  const fLabel = useLabels();
  const queryClient = useQueryClient();
  // const [hasPosition, setHasPosition] = useState(false);
  const [hasEnteries, setHasEnteries] = useState([]);
  useEffect(() => {
    // Check if any event entry has an "ee_code" other than "POSITION"
    //  console.log("🚀 ~ useEffect ~ data:", data)
    // const isHas = data?.some(personData =>
    //   personData?.registeredEvents?.some(eventEntry =>
    //     eventEntry?.eventEntries?.some(entry =>
    //       entry.ee_code === 'POSITION'
    //     )
    //   )
    // );
    // setHasPosition(isHas);

    const eventCodesArray = [...new Set(
      data.flatMap(person =>
        (person.registeredEvents || []).flatMap(event =>
          (event.eventEntries || []).map(entry =>
            entry.ee_code
          )
        )
      )
    )];
    setHasEnteries(eventCodesArray);

    const dataSportCodes = queryClient.getQueryData(["SportCodes", mirs_competition, mirs_language])?.sportCodes;
    const entryCodes = getSportCodeEntityObjects(dataSportCodes, mirs_discipline, '@Entry');
    const entryACodes = getSportCodeEntityObjects(dataSportCodes, mirs_discipline, '@EntryA');
    setSportEntryCodes(entryCodes)
    setSportEntryACodes(entryACodes)
  }, [data, mirs_competition, mirs_event, mirs_discipline, mirs_language, queryClient])

  let isAlphabeticalSort = !(data?.some(disc => disc?.organisation?.[isMobile ? "description" : "longDescription"] && disc?.organisation?.[isMobile ? "description" : "longDescription"] !== 0));
  const config = useMemo(() => getConfiguration(breakPoint, sportEntryCodes, sportEntryACodes, mirs_event, hasEnteries, isAlphabeticalSort), [breakPoint, sportEntryCodes, sportEntryACodes, mirs_event, hasEnteries, isAlphabeticalSort]);

  const renderRowSubComponent = React.useCallback(({ row }) => {
    let componentExpanded = '';
    componentExpanded = <AthleteEntriesExpanded row={row?.original} sportEntryCodes={sportEntryCodes} event={mirs_event} />
    return componentExpanded;
  }, [data, sportEntryCodes, mirs_discipline, mirs_event]);

  return (

    <MirsTable
      className="table-schedule"
      data={data}
      columns={config.columns}
      keyRow='eventUnit-code'
      initialSortBy={config.sortBy}
      id={`mirs-table-athletes`}
      showPagination={true}
      // pageSizeCustom={10}
      summaryDesc={fLabel({ data: 'SummaryEntriesPage' })}
      renderRowSubComponent={renderRowSubComponent}
    />
  )
}

export default EventEntriesAthletesTable;



function TableColumns(sportEntryCodes, sportEntryACodes, event, hasPosition, hasEnteries, isAlphabeticalSort) {
  let cols = [
    ColumnNoc({ label: "HeaderNOC", isAlphabeticalSort }),
    ColumnGender({}),
    ColumnCompetitor({}),
    ColumnDOB({}),
    //ColumnHeight({})
  ]

  sportEntryACodes.forEach(entryA => {
    if (entryA.sportCode === 'HEIGHT') {
      cols.push(ColumnHeight({}))
    } else if (entryA.order < 10) {
      if (entryA.sportCode === 'IFId') {
        cols.push(ColumnIFId(entryA));
      } else {
        cols.push(ColumnEntryA(entryA, event));
      }
    }
  })

  if (event && event === ALL_URL.ALL_EVENT) {
    cols.push(ColumnEvent({}))
  } else {
    sportEntryCodes.forEach(entry => {
      if (entry.order < 10) {
        if (entry.sportCode === 'POSITION' && hasEnteries?.find(e => e === 'POSITION')) {
          cols.push(ColumnPosition(entry, event));
        } else if (entry.sportCode !== 'POSITION' && hasEnteries?.find(e => e === entry.sportCode)) {
          cols.push(ColumnEntry(entry, event));
        }
        /*if(entry.sportCode === 'POSITION' && hasEnteries?.find(e => e==='POSITION')) {
          cols.push(ColumnPosition(entry, event))
        } else if (entry.sportCode !== 'POSITION'){
          if(hasEnteries?.find(e => e === entry.sportCode))
            cols.push(ColumnEntry(entry, event))
        }*/
      }
    })
    cols.push(ColumnExpander({ type: 'IndividualAlwaysTrue', idNode: "code", sportEntryCodes: sportEntryCodes }))
  }

  return (
    cols
  )
}

function TableColumnsSM(isAlphabeticalSort) {
  return (
    [
      ColumnNocMobile({ label: "HeaderNOC", isAlphabeticalSort }),
      ColumnCompetitorWithDOBAndGender({})
    ]
  )
}

function ColumnEvent({
  disableSort = true,
  className = 'text-start',
  headerClassName = 'text-nowrap text-start',
  label = 'HeaderEvents',
}) {
  const { mirs_discipline } = useUrlFriendlyToParameters();
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'Event',
      accessor: 'registeredEvents',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      Cell: (props) => {
        const data = props.cell.row.original;
        let events = data?.registeredEvents;
        return (<RegisteredEvents discCode={mirs_discipline} events={events} />)
      }
    }
  )
}

export function ColumnEntryA(entryA, event) {

  return (
    {
      Header: entryA.description,
      id: entryA.sportCode,
      className: 'text-start',
      headerClassName: 'text-start',
      disableSortBy: false,
      Cell: (props) => {
        let data = props.cell.row.original
        let registeredEvent = data?.registeredEvents?.find(re => re?.code === event)
        let entryVal = registeredEvent?.eventEntries?.find(ee => ee?.ee_code === entryA?.sportCode)
        return entryVal?.ee_value
      }
    }
  )
}

export function ColumnIFId(entryA) {

  return (
    {
      Header: entryA.description,
      id: entryA.sportCode,
      className: 'text-start',
      headerClassName: 'text-start',
      disableSortBy: false,
      Cell: (props) => {
        let data = props.cell.row.original
        let IFId = data?.registeredEvents?.at(0)?.ifid;
        return IFId;
      }
    }
  )
}

export function ColumnEntry(entry, event) {
  const { mirs_discipline } = useUrlFriendlyToParameters();
  return (
    {
      Header: entry.description,
      id: entry.sportCode,
      className: 'text-start',
      headerClassName: 'text-start',
      disableSortBy: false,
      Cell: (props) => {
        let data = props.cell.row.original
        let registeredEvent = data?.registeredEvents?.find(re => re.code === event)
        // let registeredEvent = data?.registeredEvents?.find(re => re !== undefined)
        let entryVal = registeredEvent?.eventEntries?.find(ee => ee?.ee_code === entry?.sportCode)
        // return entryVal?.ee_value
        // return entry?.note ? GetSportsCodefromNote(mirs_discipline, entry?.note, entryVal?.ee_value)?.description: entryVal?.ee_value
        return entry?.note ? GGetSportsCode(mirs_discipline, entry?.note, entryVal)?.description : entryVal?.ee_value
      }
    }
  )
}

export function ColumnPosition(entry, event) {
  //const positions = usePositions();
  const params = useUrlFriendlyToParameters();
  const positions = useGetPositions(params)?.positions;
  return (
    {
      Header: entry.description,
      id: entry.sportCode,
      className: 'text-start',
      headerClassName: 'text-start',
      accessor: (props) => {
        let registeredEvent = props?.registeredEvents?.find(re => re?.code === event)
        let codeEntry = registeredEvent?.eventEntries?.find(ee => ee?.ee_code === entry?.sportCode)?.ee_value
        //let position = positions(codeEntry);
        //return (position?.order)
        let position = positions?.find(pos => pos?.code === codeEntry);
        return position ? position?.order : 0; // default to 0 if position not found
      },
      disableSortBy: false,
      sortType: 'absoluteOrderNumber',
      /*sortType: (rowA, rowB) => {
        const posA = rowA.values[entry?.sportCode];
        const posB = rowB.values[entry?.sportCode];
        return posA - posB;
      },*/
      multiSort: [{ id: 'NOC', desc: false }, { id: entry.sportCode, desc: false }, { id: 'Competitor', desc: false }],
      Cell: (props) => {
        let data = props.cell.row.original
        let registeredEvent = data?.registeredEvents?.find(re => re?.code === event)
        let entryVal = registeredEvent?.eventEntries?.find(ee => ee?.ee_code === entry?.sportCode);
        //return positions(entryVal?.ee_value);
        // return entryVal?.ee_value
        let position = positions.find(pos => pos?.code === entryVal?.ee_value);
        return position ? position?.description : '';
      }
    }
  )
}

export function ColumnNoc({
  disableSort = false,
  isAlphabeticalSort = true,
  className = 'text-start ',
  headerClassName = 'text-start w-20',
  label = 'HeaderNOC',
  remove = false
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'NOC',
      accessor: (props) => {
        return isAlphabeticalSort ? props?.organisation?.longDescription?.toLowerCase() : props?.organisation?.longDescriptionOrder
      },
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      remove: remove,
      Cell: (props) => {
        let data = props.cell.row.original?.organisation
        return (<Noc {...data} longDescription={data?.description || data?.longDescription} showLongDesc={true} showImgAlt={false} />)
      }
    }
  )

}

export function ColumnNocMobile({
  disableSort = false,
  className = 'text-start',
  headerClassName = 'text-start',
  label = 'HeaderNOC',
  remove = false,
  isAlphabeticalSort = true
}) {

  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'NOC',
      accessor: (props) => {
        return isAlphabeticalSort ? props?.organisation?.description?.toLowerCase() : props?.organisation?.descriptionOrder
      },
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      remove: remove,
      Cell: (props) => {
        let data = props.cell.row.original?.organisation
        return (<Noc {...data} showLongDesc={false} />)
      }
    }
  )

}

export function ColumnHeight({
  disableSort = true,
  className = 'text-center text-wrap',
  headerClassName = 'text-center text-wrap',
  label = 'HeaderHeightMFt',
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'height',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      Cell: (props) => {
        const player = props.cell?.row?.original;

        return (getHeight(player?.height))
      }
    }
  )
}
export function ColumnGender({

  disableSort = true,
  className = 'text-center',
  headerClassName = 'text-center',
  label = 'HeaderGender',
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'Gender',
      accessor: 'personGender',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      Cell: (props) => {
        let data = props.cell.row.original?.personGender
        return (<Gender {...data} onlyDesc={true} />)
      }
    }
  )

}

export function ColumnDOB({

  disableSort = true,
  className = 'text-center',
  headerClassName = 'text-center text-nowrap',
  label = 'HeaderBirthDate',
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'DOB',
      accessor: 'birthDate',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar      
      Cell: (props) => {
        const formatDateTime = getDateTimeFormat('DayMonthShortYear');
        let { birthDate } = props.cell.row.original;
        return (<DateTime datetime={birthDate} format={formatDateTime} />)
      }
    }
  )

}


export function ColumnCompetitorWithDOBAndGender({
  disableSort = false,
}) {
  return (
    {
      Header: <Stack direction='vertical'>
        <Label data={"LabelName"} />
        <Stack direction="horizontal">
          <Label data={"HeaderBirthDate"} />
          <span> / </span>
          <Label data={"HeaderGender"} />
        </Stack>
      </Stack>,
      accessor: (props) => ((props?.organisation?.code || " ") + (props?.name?.toUpperCase() || "")),
      className: '',
      headerClassName: '',
      disableSortBy: disableSort,
      id: 'GenderAndName',
      Cell: (props) => {
        const row = props.cell.row.original
        const formatDateTime = getDateTimeFormat('DayMonthShortYear');
        return (
          <Stack direction='vertical'>
            <Competitor {...row} hideNoc={true} hidePicture={false} key={`participant-${row?.code}`} />
            <Stack gap={1} direction='horizontal'>
              <DateTime datetime={row?.birthDate} format={formatDateTime} />
              <span>/</span>
              <Gender {...row?.personGender} onlyDesc={true} />
            </Stack>
          </Stack>
        )
      }
    }
  )
}

export function ColumnCompetitor({
  disableSort = false,
  className = 'schedule-discipline text-start',
  headerClassName = 'text-start',
  label = "LabelName"
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'Competitor',
      accessor: (props) => (props?.name?.toUpperCase()),
      //sortType: "absoluteOrderString",
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      Cell: (props) => {
        const row = props.cell.row.original
        return (
          <Competitor {...row} hideNoc={true} hidePicture={false} key={`participant-${row?.code}`} />
        )
      }
    }
  )
}